import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { EASE } from "@/components/landing/Reveal";

export const Breadcrumbs = ({ items, light = false }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs">
    <ol className={`flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${light ? "text-ivory/65" : "text-slateblue"}`}>
      {items.map((item, i) => (
        <li key={item.label} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3 opacity-60" aria-hidden="true" />}
          {item.to ? (
            <Link to={item.to} data-testid={`breadcrumb-link-${i}`} className={`transition-colors ${light ? "hover:text-ivory" : "hover:text-marian"}`}>
              {item.label}
            </Link>
          ) : (
            <span className={light ? "text-gold" : "text-midnight"}>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const MaskedLines = ({ lines, className = "", delay = 0.2 }) => (
  <>
    {lines.map((line, i) => (
      <span key={line} className={`block overflow-hidden pb-1 ${className}`}>
        <motion.span
          className="block"
          initial={{ y: "115%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1, delay: delay + i * 0.13, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </>
);

export default function PageIntro({ crumbs, eyebrow, lines, children, aside, compact = false }) {
  return (
    <section className="relative overflow-hidden border-b border-marian/10" data-testid="page-intro">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div className={`relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 ${compact ? "pb-8 pt-6 sm:pb-12 sm:pt-10" : "pb-14 pt-10 sm:pb-20 sm:pt-14"}`}>
        <Breadcrumbs items={crumbs} />
        <div className={`grid lg:grid-cols-[1.4fr_0.6fr] lg:items-end ${compact ? "mt-6 gap-6" : "mt-10 gap-10"}`}>
          <div>
            <motion.p
              initial={compact ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="font-mono text-xs uppercase tracking-[0.34em] text-gold"
              data-testid="page-intro-eyebrow"
            >
              {eyebrow}
            </motion.p>
            <h1 data-testid="page-intro-title" className="mt-5 font-serif text-4xl font-semibold leading-[1.04] text-midnight sm:text-5xl lg:text-6xl">
              {compact ? lines.map((line) => <span key={line} className="block pb-1">{line}</span>) : <MaskedLines lines={lines} />}
            </h1>
            {children && (
              <motion.p
                initial={compact ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className={`${compact ? "mt-4" : "mt-7"} max-w-2xl text-base leading-relaxed text-charcoal/85 sm:text-lg`}
                data-testid="page-intro-description"
              >
                {children}
              </motion.p>
            )}
          </div>
          {aside && (
            <motion.div
              initial={compact ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            >
              {aside}
            </motion.div>
          )}
        </div>
      </div>
      <div className="gold-rule mx-auto max-w-5xl opacity-60" aria-hidden="true" />
    </section>
  );
}
